import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { applyMode, Mode } from '@amzn/awsui-global-styles';
import { logger } from 'src/analytics/KatalLogger';
import {
  EMBED_TO_PARENT_MESSAGES,
  PARENT_TO_EMBED_MESSAGES,
  EmbedToParentMessage,
  ParentToEmbedMessage
} from 'src/constants/embed-message-constants';
import { toggleTheme } from 'src/app/AppMetadataSlice';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { useEmbedParams } from './useEmbedParams';
import { navigateWithEmbedParams } from 'src/utils/navigation-utils';

/**
 * Hook that enables cross-frame communication between FinSuite (parent) and this embedded app.
 *
 * When the app is embedded in FinSuite's iframe:
 * 1. Announces readiness by sending FINSUITE_EMBED_READY to the parent window
 * 2. Listens for navigation commands from the parent (e.g., FINSUITE_NAVIGATE_HOME)
 * 3. Routes internally using React Router — no iframe remount needed
 *
 * When the app is NOT embedded, this hook is a no-op.
 *
 * To adopt this in another embedded app:
 * - Import and call this hook in your app's router component (inside BrowserRouter)
 * - Ensure your app has the embed-message-constants and navigateWithEmbedParams utility
 */
export const useParentMessageListener = (): void => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isEmbedded } = useEmbedParams();

  // Announce readiness to parent window
  useEffect(() => {
    if (!isEmbedded) return;

    try {
      const readyMessage: EmbedToParentMessage = { type: EMBED_TO_PARENT_MESSAGES.EMBED_READY };
      window.parent.postMessage(readyMessage, '*');
      logger.info('Sent FINSUITE_EMBED_READY to parent window');
    } catch (error) {
      logger.error('Failed to send embed ready message to parent', { error });
    }
  }, [isEmbedded]);

  // Listen for navigation commands from parent
  useEffect(() => {
    if (!isEmbedded) return;

    const handleMessage = (event: MessageEvent) => {
      // Ignore messages that aren't our protocol
      if (!event.data?.type) return;

      const message = event.data as ParentToEmbedMessage;

      switch (message.type) {
        case PARENT_TO_EMBED_MESSAGES.NAVIGATE_HOME:
          logger.info('Received FINSUITE_NAVIGATE_HOME from parent, navigating to home');
          navigateWithEmbedParams(navigate, '/');
          break;
        case PARENT_TO_EMBED_MESSAGES.THEME_CHANGE: {
          const isDarkMode = !!message.payload?.isDarkMode;
          logger.info(`Received FINSUITE_THEME_CHANGE from parent, switching to ${isDarkMode ? 'dark' : 'light'} mode`);
          dispatch(toggleTheme(isDarkMode));
          // Apply Cloudscape visual mode directly since XptTopNavigation (which normally calls applyMode)
          // is not rendered when embedded.
          applyMode(isDarkMode ? Mode.Dark : Mode.Light);
          break;
        }
        default:
          // Ignore unrecognized message types silently
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    logger.info('Parent message listener registered');

    return () => {
      window.removeEventListener('message', handleMessage);
      logger.info('Parent message listener unregistered');
    };
  }, [isEmbedded, navigate, dispatch]);
};
